import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Download,
  Gauge,
  Pencil,
  Plus,
  Search,
  Timer,
  UserPlus,
} from 'lucide-react';
import {
  ArchivedToggle,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  InfoHint,
  MonthPicker,
  PageHeader,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  StatCard,
  StatIcon,
  StatusPill,
  sortRows,
  type DataTableColumn,
  type DataTableSort,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatHours, formatMoney, formatMonth, formatPercent } from '../../lib/format';
import {
  useOrgUtilization,
  useTeamList,
  type Person,
  type TeamPerson,
  type UtilizationScope,
} from './api';
import {
  Avatar,
  CapacityBar,
  CapacityBarKey,
  CostModelChip,
  InProgressPill,
  MonthlySpark,
  UTILIZATION_TARGET,
  UtilizationTriageChips,
  bandOf,
  compactMoney,
  countBands,
  downloadCsv,
  formatHoursFixed,
  paddedWindow,
  summarizeMoney,
  utilizationHex,
  type RowFilter,
} from './components';

import { csvFilename } from '../../lib/csv';
import { PersonModal } from './PersonModal';
import { cadencePhrase, useCadenceCounts } from './time-policy';

/** Neutral slate for the hours spark — hours carry no good/bad polarity. */
const AVG_HOURS_HEX = '#8494a5';

type TypeFilter = UtilizationScope;

const SCOPE_LABEL: Record<TypeFilter, string> = {
  ALL: 'Team utilization',
  STAFF: 'Staff utilization',
  CONTRACTOR: 'Contractor utilization',
};

/**
 * ⚠ **TWO RATIOS OF THE SAME BILLABLE HOURS SIT IN THIS KPI STRIP, AND THEY
 * MUST STAY TELLABLE APART.** Utilization divides by CAPACITY; billable share
 * divides by what was TRACKED. They were 49% and 63% for the same 8 staff in
 * the same month, and `/dashboard` once printed the 63% one under this tile's
 * own title.
 *
 * The arbitration used to be a four-line paragraph under the strip. Jesse,
 * 2026-08-13, reading the real screen: *"if this description needs to be
 * available make it in (i) icon info popups."* So the DENOMINATOR stays on each
 * tile as a short label — `billable ÷ capacity`, `billable ÷ tracked`, which is
 * the whole of what a reader needs to not confuse them — and the paragraph
 * moved in here. A label, not a lesson.
 *
 * Both bodies live in one place so the two tiles cannot drift into describing
 * each other differently, exactly as `clients/revenue-basis.ts` does for the
 * revenue pair.
 */
function UtilizationBasis({
  billableHours,
  capacityHours,
  scopeNoun,
}: {
  billableHours: number;
  capacityHours: number;
  scopeNoun: string;
}) {
  return (
    <>
      <p>
        Billable hours ÷ the capacity available —{' '}
        <span className="num font-semibold text-secondary">
          {formatHours(billableHours, { fixed: true })} h
        </span>{' '}
        of the{' '}
        <span className="num font-semibold text-secondary">
          {formatHours(capacityHours, { fixed: true })} h
        </span>{' '}
        available to {scopeNoun === 'team' ? 'the team' : scopeNoun}. It is the only figure in this
        strip the {formatPercent(UTILIZATION_TARGET, { decimals: 0 })} target judges.
      </p>
      <p className="mt-2">
        Not the same as <strong className="font-semibold">billable share</strong>, two tiles right,
        which divides the same billable hours by the hours actually tracked.{' '}
        <Link to="/dashboard" className="font-semibold text-accent-700 hover:underline">
          The dashboard
        </Link>{' '}
        prints both, for staff.
      </p>
    </>
  );
}

function BillableShareBasis({
  billableHours,
  totalHours,
}: {
  billableHours: number;
  totalHours: number;
}) {
  return (
    <>
      <p>
        Billable hours ÷ the hours actually tracked —{' '}
        <span className="num font-semibold text-secondary">
          {formatHours(billableHours, { fixed: true })} h
        </span>{' '}
        of{' '}
        <span className="num font-semibold text-secondary">
          {formatHours(totalHours, { fixed: true })} h
        </span>
        . It carries no target: it says how much of a logged day was sellable, not how much of the
        week was sold.
      </p>
      <p className="mt-2">
        Not the same as <strong className="font-semibold">utilization</strong>, the first tile in
        this strip, which divides the same billable hours by capacity.
      </p>
    </>
  );
}

/**
 * Keeps the (i) on the same line as the last word of a label. A KPI label wraps
 * at 390px ("TEAM / UTILIZATION"), and an icon left alone on the next line
 * reads as a stray mark rather than as the affordance it is.
 */
function labelWithHint(text: string, hint: ReactNode) {
  const words = text.split(' ');
  const last = words.pop() ?? text;
  return (
    <>
      {words.length > 0 && `${words.join(' ')} `}
      <span className="whitespace-nowrap">
        {last}
        {hint}
      </span>
    </>
  );
}

/** How the current sort reads in prose — the caption must never lie. */
const SORT_CAPTION: Record<string, { asc: string; desc: string }> = {
  person: { asc: 'A–Z', desc: 'Z–A' },
  billRate: { asc: 'lowest bill rate first', desc: 'highest bill rate first' },
  hours: { asc: 'fewest hours first', desc: 'most hours first' },
  utilization: { asc: 'least utilized first', desc: 'most utilized first' },
  cost: { asc: 'lowest cost first', desc: 'highest cost first' },
  margin: { asc: 'worst margin first', desc: 'best margin first' },
};

const DEFAULT_SORT: DataTableSort = { key: 'utilization', dir: 'asc' };

// ---------------------------------------------------------------------------
// URL state — every filter is a query param, so "July, over-capacity only" is
// bookmarkable, shareable and reachable with the back button (Harvest puts its
// report params in the URL; for a reporting tool that is table stakes).
// ---------------------------------------------------------------------------

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const TYPES: TypeFilter[] = ['ALL', 'STAFF', 'CONTRACTOR'];
const ROW_FILTERS: RowFilter[] = ['negative', 'idle', 'low', 'target', 'over', 'unmeasured'];

function parseSort(raw: string | null): DataTableSort {
  if (!raw) return DEFAULT_SORT;
  const [key, dir] = raw.split(':');
  if (!key || (dir !== 'asc' && dir !== 'desc')) return DEFAULT_SORT;
  return { key, dir };
}

function serializeSort(sort: DataTableSort): string | null {
  if (!sort) return null;
  const asString = `${sort.key}:${sort.dir}`;
  return asString === `${DEFAULT_SORT!.key}:${DEFAULT_SORT!.dir}` ? null : asString;
}

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------

/**
 * The shared StatCard with a slot beneath the value — the hero strip is the
 * most valuable real estate on the page and a card that only says one number is
 * wasting it.
 *
 * This was a hand-rolled "local twin" of `StatCard` until the shared primitive
 * grew the two slots it actually needed (`footer`, `deltaSuffix`). A second
 * copy of the app's most-repeated component is how four money screens end up
 * with four card paddings.
 */
function TeamStatCard({
  label,
  value,
  valueClassName,
  hint,
  delta,
  deltaSuffix,
  icon,
  loading = false,
  error = false,
  errorMessage,
  onRetry,
  children,
}: {
  label: ReactNode;
  value: ReactNode;
  valueClassName?: string;
  hint?: ReactNode;
  delta?: number;
  deltaSuffix?: string;
  icon: ReactNode;
  loading?: boolean;
  /** ⚠ Forwarded to `StatCard` — a tile whose query failed must SAY so. This
   *  wrapper lists its props explicitly, so anything it does not name is
   *  silently dropped: the error state was passed in and swallowed here before
   *  these three lines existed. */
  error?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  return (
    <StatCard
      label={label}
      value={value}
      valueClassName={valueClassName}
      // Wraps rather than truncates: a KPI hint that ends in "…" is a KPI hint
      // that failed to say anything.
      hint={hint}
      hintLines={2}
      delta={delta}
      deltaSuffix={deltaSuffix}
      icon={icon}
      loading={loading}
      error={error}
      errorMessage={errorMessage}
      onRetry={onRetry}
      footer={children}
    />
  );
}

/** Shared footer under a spark: the span it covers, and the scale it is drawn on. */
function SparkFooter({ months, scale }: { months: Array<{ month: string }>; scale: string }) {
  return (
    <p className="mt-1.5 flex items-center justify-between gap-2 text-caption text-grey-400">
      <span className="whitespace-nowrap">
        {months.length > 0
          ? `${formatMonth(`${months[0]!.month}-01`)} — ${formatMonth(`${months[months.length - 1]!.month}-01`)}`
          : 'no history yet'}
      </span>
      <span className="num hidden whitespace-nowrap xl:inline">{scale}</span>
    </p>
  );
}

/** Revenue vs cost as two bars on a shared scale — the shape of the margin. */
function RevenueCostBars({
  revenue,
  cost,
  className,
}: {
  revenue: number;
  cost: number;
  className?: string;
}) {
  const top = Math.max(revenue, cost, 1);
  const row = (label: string, short: string, amount: number, fill: string) => (
    <div className="flex items-center gap-1.5 sm:gap-2">
      <span className="shrink-0 text-caption text-grey-500">
        <span className="sm:hidden">{short}</span>
        <span className="hidden w-11 sm:inline-block">{label}</span>
      </span>
      <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-grey-100">
        <span
          className={cn('block h-full rounded-full', fill)}
          style={{ width: `${(Math.max(0, amount) / top) * 100}%` }}
        />
      </span>
      <span className="num shrink-0 text-right text-caption font-semibold text-secondary-700">
        <span className="sm:hidden">{compactMoney(amount)}</span>
        <span className="hidden w-16 sm:inline-block">{formatMoney(amount, { whole: true })}</span>
      </span>
    </div>
  );
  return (
    <div className={cn('mt-3 space-y-1.5', className)}>
      {row('Revenue', 'Rev', revenue, 'bg-success')}
      {row('Cost', 'Cost', cost, 'bg-secondary-500')}
    </div>
  );
}

export function TeamListPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // ---- state, read straight off the URL ------------------------------------
  const monthParam = params.get('month');
  const month = monthParam && MONTH_RE.test(monthParam) ? monthParam : undefined;
  const rawType = params.get('type');
  const typeFilter: TypeFilter = TYPES.includes(rawType as TypeFilter)
    ? (rawType as TypeFilter)
    : 'ALL';
  const rawBand = params.get('band');
  const bandFilter: RowFilter | null = ROW_FILTERS.includes(rawBand as RowFilter)
    ? (rawBand as RowFilter)
    : null;
  const search = params.get('q') ?? '';
  const showArchived = params.get('archived') === '1';
  const sort = parseSort(params.get('sort'));

  const patch = useCallback(
    (next: Record<string, string | null>, replace = false) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(next)) {
            if (value == null || value === '') p.delete(key);
            else p.set(key, value);
          }
          return p;
        },
        { replace },
      );
    },
    [setParams],
  );

  const list = useTeamList(month);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Person | undefined>(undefined);
  // The "/" shortcut lives in SearchInput now, so every list page has it.

  // The KPI strip is scoped to the same population as the rows: filtering to
  // Contractors must not leave four staff-only headline numbers on screen.
  const utilization = useOrgUtilization(typeFilter);

  /**
   * ⚠ **THESE THREE TILES HAVE THEIR OWN REQUEST, AND IT CAN FAIL ON ITS
   * OWN.** `/api/team/utilization` fired four queries in one `Promise.all`, so
   * on a Postgres at its connection cap it was the first thing on this page to
   * break — and the page said nothing: a shimmer while React Query retried,
   * then a silent "—", which is the same glyph this app uses for *no data*.
   * A KPI that has given up must say so and offer the way back (`StatCard`
   * grew `error`/`onRetry` for exactly this, and `/invoices` and `/support`
   * had the same hole).
   */
  const utilFailed = {
    error: utilization.isError,
    errorMessage:
      utilization.error instanceof Error ? utilization.error.message : undefined,
    onRetry: () => void utilization.refetch(),
  };

  const activeMonth = list.data?.month ?? month;
  const monthLabel = activeMonth ? formatMonth(`${activeMonth}-01`) : '';
  const monthInProgress = list.data?.inProgress ?? false;
  const utilMonths = utilization.data?.months ?? [];
  const utilIndex = utilMonths.findIndex((m) => m.month === activeMonth);
  const current =
    utilMonths.find((m) => m.month === activeMonth) ?? (month ? undefined : utilization.data?.current);
  const previous = utilIndex > 0 ? utilMonths[utilIndex - 1] : undefined;
  // Both sparks are drawn on a padded window around their own data (and the
  // utilization one always contains the 75% target), with the scale printed
  // beneath — a 0-anchored 12-bar spark of a 56–74% series is twelve identical
  // blocks and no two months can be ranked.
  const utilWindow = useMemo(
    () =>
      paddedWindow(
        utilMonths.map((m) => m.utilization).filter((v): v is number => v != null),
        { step: 0.05, pad: 0.03, include: UTILIZATION_TARGET },
      ),
    [utilMonths],
  );
  const avgWindow = useMemo(
    () =>
      paddedWindow(
        utilMonths.map((m) => m.avgHoursPerPersonPerWeek).filter((v): v is number => v != null),
        { step: 5, pad: 1 },
      ),
    [utilMonths],
  );

  const people = list.data?.people ?? [];
  const archivedCount = people.filter((p) => !p.isActive).length;

  const visible = useMemo(
    () => people.filter((p) => showArchived || p.isActive),
    [people, showArchived],
  );
  const staffCount = visible.filter((p) => p.type === 'STAFF').length;
  const contractorCount = visible.length - staffCount;
  const scopeNoun =
    typeFilter === 'ALL' ? 'team' : typeFilter === 'STAFF' ? 'staff' : 'contractors';

  /** The population the KPI cards describe: type filter, but not the search. */
  const scoped = useMemo(
    () => visible.filter((p) => typeFilter === 'ALL' || p.type === typeFilter),
    [visible, typeFilter],
  );
  const money = useMemo(() => summarizeMoney(scoped), [scoped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scoped.filter(
      (p) => !q || p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q),
    );
  }, [scoped, search]);

  // Band counts come from the search/type-filtered set (so the chips describe
  // what you are looking at) but NOT the band filter itself — otherwise every
  // other chip would read 0 the moment you picked one.
  const bandCounts = useMemo(() => countBands(filtered), [filtered]);
  /**
   * How many of the people ON SCREEN are tagged as time trackers.
   *
   * Scoped to `filtered` — the search and type filters — for the same reason
   * the band chips are: a figure that describes a different set than the rows
   * under it is a figure nobody can check.
   */
  /**
   * ⚠ **A TIME TRACKER WITH NO SOLID ACCOUNT GETS NO TIMER, SILENTLY.**
   *
   * The timer authenticates as the person, so an unlinked one is refused and the
   * widget renders nothing — by design, because most people reaching the app bar
   * are hub client users who must never see a time tracker. The consequence for
   * administration is that ticking "tracks time" on somebody without an account
   * enables NOTHING and looks exactly like success.
   *
   * This screen used to print a bare "7 of 10 track time" while four of those
   * seven had no account. So the figure is now split: how many will actually get
   * a timer, and how many are waiting on an account.
   */
  const trackers = useMemo(() => {
    const on = filtered.filter((p) => p.tracksTime);
    return {
      ready: on.filter((p) => p.hasSolidAccount).length,
      waiting: on.filter((p) => !p.hasSolidAccount).length,
      // Everyone who tracks time needs an address on file (Jesse, 2026-10-07).
      noAddress: on.filter((p) => !p.hasAddress).length,
      total: filtered.length,
    };
  }, [filtered]);
  const cadenceCounts = useCadenceCounts(filtered);
  const chipMoney = useMemo(() => summarizeMoney(filtered), [filtered]);
  const rows = useMemo(() => {
    if (bandFilter == null) return filtered;
    if (bandFilter === 'negative') return filtered.filter((p) => p.currentMonth.margin < 0);
    return filtered.filter((p) => bandOf(p.currentMonth.utilization) === bandFilter);
  }, [filtered, bandFilter]);

  const sortCaption = sort
    ? (SORT_CAPTION[sort.key]?.[sort.dir] ?? 'custom order')
    : 'in the default order';

  const openCreate = () => {
    setEditing(undefined);
    setModalOpen(true);
  };
  const openEdit = (person: Person) => {
    setEditing(person);
    setModalOpen(true);
  };

  const exportCsv = () => {
    const totals = summarizeMoney(rows);
    // The order the table is actually showing, from the table's own comparator.
    const ordered = sortRows(rows, columns, sort);
    downloadCsv(
      csvFilename({
        entity: 'team',
        view: [
          typeFilter === 'ALL' ? '' : typeFilter.toLowerCase(),
          bandFilter ? `band-${bandFilter}` : '',
          showArchived ? 'with-archived' : '',
          search ? `q-${search}` : '',
        ]
          .filter(Boolean)
          .join('-'),
        period: activeMonth,
      }),
      [
        [
          'Name',
          'Email',
          'Type',
          'Cost model',
          'Bill rate',
          'Weekly capacity (h)',
          'Tracks time',
          'Timesheets',
          'Month',
          'Tracked hours',
          'Billable hours',
          'Capacity hours',
          'Utilization (billable/capacity)',
          'Billable share (billable/tracked)',
          'Revenue',
          'Cost',
          'Margin',
          'Status',
        ],
        ...ordered.map((p) => {
          const m = p.currentMonth;
          return [
            p.name,
            p.email,
            p.type === 'STAFF' ? 'Staff' : 'Contractor',
            p.costModel === 'HOURLY' ? 'Hourly' : 'Flat monthly',
            p.defaultBillRate ?? '',
            p.weeklyCapacityHours ?? '',
            p.tracksTime ? 'yes' : 'no',
            p.timesheetCadence.toLowerCase(),
            m.month,
            m.hours.toFixed(2),
            m.billableHours.toFixed(2),
            m.capacityHours.toFixed(2),
            m.utilization != null ? m.utilization.toFixed(4) : '',
            m.billableShare != null ? m.billableShare.toFixed(4) : '',
            m.revenue.toFixed(2),
            m.cost.toFixed(2),
            m.margin.toFixed(2),
            p.isActive ? 'Active' : 'Archived',
          ];
        }),
        [
          `Total (${rows.length})`,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          activeMonth ?? '',
          '',
          '',
          '',
          '',
          '',
          totals.revenue.toFixed(2),
          totals.cost.toFixed(2),
          totals.margin.toFixed(2),
          '',
        ],
      ],
    );
  };

  const columns: Array<DataTableColumn<TeamPerson>> = [
    {
      key: 'person',
      header: 'Person',
      sortable: true,
      sortValue: (p) => p.name,
      // Caps stay modest on purpose: a truncate cell's max-w IS its min-content
      // contribution, so an oversized name column silently pushes the money
      // column off the right edge of the table (see CLAUDE.md).
      className: 'max-w-[11rem] px-3 sm:px-4 md:max-w-[13rem] lg:max-w-[11rem] xl:max-w-[13rem]',
      // A real anchor, not a row onClick: the page's primary action has to be
      // reachable by keyboard and openable in a new tab (cmd/middle click).
      render: (p) => (
        <>
          <Link
            to={`/team/${p.id}`}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'group -mx-1 flex items-center gap-2.5 rounded-md px-1 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 sm:gap-3',
              !p.isActive && 'opacity-60',
            )}
          >
            <Avatar name={p.name} />
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 font-semibold text-secondary group-hover:text-accent">
                <span className="truncate">{p.name}</span>
                {!p.isActive && <StatusPill status="neutral" label="Archived" dot={false} />}
              </span>
              <span className="flex items-center gap-1.5 text-caption text-grey-500">
                <span
                  className={cn(
                    'shrink-0 font-semibold uppercase tracking-wide',
                    p.type === 'STAFF' ? 'text-accent-700' : 'text-status-partial',
                  )}
                >
                  {p.type === 'STAFF' ? 'Staff' : 'Contractor'}
                </span>
                {/* The time policy rides in the meta line — a column would
                    break this table's width budget. Only trackers carry it. */}
                {p.tracksTime && (
                  <span className="inline-flex shrink-0 items-center gap-0.5 text-grey-500">
                    <Timer className="h-3 w-3" aria-hidden />
                    {cadencePhrase(p.timesheetCadence)}
                    {!p.timesheetRequired && p.timesheetCadence !== 'NEVER' && ' (optional)'}
                  </span>
                )}
                <span className="hidden truncate sm:inline">{p.email}</span>
              </span>
            </span>
          </Link>
          {/* Margin rides along on mobile so its own column can drop out rather
              than be squashed off the right edge — the money must survive to
              390px, it is the point of the row. */}
          <span className="num mt-1 flex items-baseline gap-1 text-caption sm:hidden">
            <span
              className={cn(
                'font-bold',
                p.currentMonth.margin < 0 ? 'num-negative' : 'text-secondary',
              )}
            >
              {formatMoney(p.currentMonth.margin, { whole: true, parens: true })}
            </span>
            <span className="text-grey-500">margin</span>
          </span>
        </>
      ),
    },
    {
      key: 'costBasis',
      header: 'Cost basis',
      // The widest column and the only pure reference value (a static rate, not
      // a monthly measure), so it is the first to yield: between 1280 and 1400
      // the money columns keep the room instead. Measured, not guessed — see
      // CLAUDE.md on min-content widths pushing the money column off the edge.
      className: 'hidden min-[1400px]:table-cell min-[1400px]:px-2',
      render: (p) => <CostModelChip {...p} />,
    },
    {
      key: 'billRate',
      header: 'Bill rate',
      align: 'right',
      className: 'hidden whitespace-nowrap xl:table-cell xl:px-2',
      sortable: true,
      sortValue: (p) => p.defaultBillRate ?? -1,
      render: (p) =>
        p.defaultBillRate != null ? (
          <span>{formatMoney(p.defaultBillRate, { whole: true })}/hr</span>
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      className: 'hidden sm:table-cell sm:px-3 xl:px-2',
      sortable: true,
      sortValue: (p) => p.currentMonth.hours,
      render: (p) => (
        <span className="block">
          <span className="font-semibold">{formatHoursFixed(p.currentMonth.hours)}</span>
          {/* Billable share, spelled out as a share OF TRACKED so it cannot be
              read as the utilization percentage sitting next to it. */}
          <span
            className="hidden whitespace-nowrap text-caption font-normal text-grey-500 lg:block"
            title="Billable hours, and their share of tracked time"
          >
            {formatHoursFixed(p.currentMonth.billableHours)} billable
            {p.currentMonth.billableShare != null && (
              <> · {formatPercent(p.currentMonth.billableShare, { decimals: 0 })} of tracked</>
            )}
          </span>
        </span>
      ),
    },
    {
      key: 'utilization',
      header: <span title="Billable hours ÷ available capacity">Utilization</span>,
      align: 'right',
      className: 'px-3 sm:px-4',
      sortable: true,
      // Unmeasured people (no capacity set) sink to the bottom rather than
      // masquerading as the most under-utilized.
      sortValue: (p) => p.currentMonth.utilization ?? Number.POSITIVE_INFINITY,
      render: (p) => {
        const m = p.currentMonth;
        return (
          <span className="inline-flex flex-col items-end gap-0.5">
            <CapacityBar
              name={p.name}
              billableHours={m.billableHours}
              trackedHours={m.hours}
              capacityHours={m.capacityHours}
              utilization={m.utilization}
              barClassName="w-12 sm:w-20 xl:w-24 2xl:w-32"
            />
            <span className="num whitespace-nowrap text-caption text-grey-500">
              {m.capacityHours > 0 ? (
                <>
                  {formatHoursFixed(m.billableHours)} / {formatHoursFixed(m.capacityHours)} h
                  <span className="hidden 2xl:inline"> capacity</span>
                </>
              ) : (
                'no capacity set'
              )}
            </span>
          </span>
        );
      },
    },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      className: 'hidden xl:table-cell xl:px-2',
      sortable: true,
      sortValue: (p) => p.currentMonth.cost,
      render: (p) => formatMoney(p.currentMonth.cost, { whole: true }),
    },
    {
      key: 'margin',
      // The money answer the utilization meter cannot give: a 24%-utilized
      // salaried person is losing $1.8k while the red 115% contractor is the
      // most profitable name on the roster.
      header: <span title="Revenue at bill rates − cost, this month">Margin</span>,
      align: 'right',
      className: 'hidden px-3 sm:table-cell sm:px-4',
      sortable: true,
      sortValue: (p) => p.currentMonth.margin,
      render: (p) => {
        const m = p.currentMonth;
        return (
          <span className="block">
            <span className={cn('font-bold', m.margin < 0 ? 'num-negative' : 'text-secondary')}>
              {formatMoney(m.margin, { whole: true, parens: true })}
            </span>
            <span className="hidden whitespace-nowrap text-caption font-normal text-grey-500 lg:block">
              {formatMoney(m.revenue, { whole: true })} revenue
            </span>
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      className: 'hidden w-10 px-2 sm:table-cell sm:px-4',
      render: (p) => (
        <button
          type="button"
          className="btn-icon text-grey-400 hover:text-secondary"
          aria-label={`Edit ${p.name}`}
          onClick={(e) => {
            e.stopPropagation();
            openEdit(p);
          }}
        >
          <Pencil className="h-4 w-4" aria-hidden />
        </button>
      ),
    },
  ];

  if (list.isError) {
    return (
      <div>
        <PageHeader title="Team" subtitle="People, capacity & margin" />
        <ErrorState
          card
          message={list.error instanceof Error ? list.error.message : 'Failed to load the team'}
          onRetry={() => void list.refetch()}
        />
      </div>
    );
  }

  /**
   * ⚠ A COUNT IS A CLAIM, AND WHILE THE ROSTER IS LOADING WE HAVE NOTHING TO
   * CLAIM. The strip asserted "Everyone 0 · Staff 0 · Contractors 0" for as
   * long as the request took and then jumped to 12 / 8 / 4 — a filter bar
   * saying, with numbers, that this org has no people. `count` is optional on
   * `SegmentedControl` precisely so a label can stand alone until its figure
   * is real; the table beside it already shows a skeleton rather than "0 rows".
   */
  const counted = !list.isPending;
  const filterChips: Array<{ value: TypeFilter; label: string; count?: number }> = [
    { value: 'ALL', label: 'Everyone', count: counted ? visible.length : undefined },
    { value: 'STAFF', label: 'Staff', count: counted ? staffCount : undefined },
    {
      value: 'CONTRACTOR',
      label: 'Contractors',
      count: counted ? contractorCount : undefined,
    },
  ];

  const setType = (next: TypeFilter) =>
    patch({ type: next === 'ALL' ? null : next, band: null });

  const avgDelta =
    current?.avgHoursPerPersonPerWeek != null &&
    previous?.avgHoursPerPersonPerWeek != null &&
    previous.avgHoursPerPersonPerWeek > 0
      ? current.avgHoursPerPersonPerWeek / previous.avgHoursPerPersonPerWeek - 1
      : undefined;
  // A month-to-date hour total against a full previous month is not a delta,
  // it is an artifact of the calendar — so it is only shown once the month ends.
  const billableDelta =
    !monthInProgress && current && previous && previous.billableHours > 0
      ? current.billableHours / previous.billableHours - 1
      : undefined;
  // "vs Jul", not "vs Jul 2026": the year is already in the page subtitle and
  // the long form pushed the card's hint into an ellipsis.
  const prevLabel = previous
    ? `vs ${new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(
        new Date(`${previous.month}-01T12:00:00Z`),
      )}`
    : undefined;

  return (
    <div>
      <PageHeader
        title="Team"
        subtitle={
          monthLabel
            ? `People, capacity & margin — ${monthLabel}${monthInProgress ? ' (month to date)' : ''}`
            : 'People, capacity & margin'
        }
        actions={
          <>
            {/* The period control lives in the page header, the way it does on
                the dashboard, /support, /budgets and the P&L. It used to sit
                down in the filter bar, so the one control that re-scopes the
                whole page was in a different place here than anywhere else. */}
            {list.data && (
              <MonthPicker
                month={list.data.month}
                max={list.data.latestMonth}
                min={utilMonths[0]?.month}
                onChange={(next) => patch({ month: next === list.data!.latestMonth ? null : next })}
              />
            )}
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" aria-hidden /> New person
            </Button>
          </>
        }
      />

      {/* Org summary strip: utilization, the money it produced, and the hours
          behind both. */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <TeamStatCard
          label={labelWithHint(
            SCOPE_LABEL[typeFilter],
            current && (
              <InfoHint label="Utilization">
                <UtilizationBasis
                  billableHours={current.billableHours}
                  capacityHours={current.capacityHours}
                  scopeNoun={scopeNoun}
                />
              </InfoHint>
            ),
          )}
          value={
            current?.utilization != null
              ? formatPercent(current.utilization, { decimals: 0 })
              : '—'
          }
          // ⚠ THE DENOMINATOR IS THE LABEL. Two tiles in this strip divide the
          // same billable hours by different things; the hint is what keeps
          // them apart at a glance, and the paragraph that used to arbitrate
          // them lives behind the (i) above.
          hint={`billable ÷ capacity · target ${formatPercent(UTILIZATION_TARGET, { decimals: 0 })}`}
          icon={<StatIcon kind="utilization" />}
          loading={utilization.isPending}
          {...utilFailed}
        >
          <MonthlySpark
            className="mt-3"
            months={utilMonths}
            value={(m) => m.utilization}
            color={(v) => utilizationHex(v)}
            window={utilWindow}
            reference={UTILIZATION_TARGET}
            selectedMonth={activeMonth}
            tooltip={(m, v) =>
              `${formatMonth(`${m.month}-01`)} — ${
                v != null ? `${formatPercent(v, { decimals: 0 })} of capacity` : 'no data'
              }`
            }
            ariaLabel={`Monthly utilization against capacity on a ${formatPercent(utilWindow.lo, {
              decimals: 0,
            })} to ${formatPercent(utilWindow.hi, { decimals: 0 })} scale: ${utilMonths
              .map(
                (m) =>
                  `${formatMonth(`${m.month}-01`)} ${
                    m.utilization != null
                      ? formatPercent(m.utilization, { decimals: 0 })
                      : 'no data'
                  }`,
              )
              .join(', ')}`}
          />
          <SparkFooter
            months={utilMonths}
            scale={`scale ${formatPercent(utilWindow.lo, { decimals: 0 })}–${formatPercent(utilWindow.hi, { decimals: 0 })}`}
          />
        </TeamStatCard>
        <TeamStatCard
          label={
            <>
              Margin
              <span className="hidden sm:inline">
                {' · '}
                {monthInProgress ? 'month to date' : monthLabel || 'month'}
              </span>
            </>
          }
          value={formatMoney(money.margin, { whole: true, parens: true })}
          valueClassName={money.margin < 0 ? 'num-negative' : undefined}
          hint={
            money.negativeCount > 0 ? (
              <span className="font-semibold text-status-late">
                {money.negativeCount} losing money ·{' '}
                {formatMoney(money.negativeMargin, { whole: true, parens: true })}
              </span>
            ) : (
              `${scoped.length} ${scopeNoun === 'team' ? 'people' : scopeNoun} · everyone above water`
            )
          }
          icon={<StatIcon kind="margin" />}
          loading={list.isPending}
        >
          <RevenueCostBars revenue={money.revenue} cost={money.cost} />
        </TeamStatCard>
        <TeamStatCard
          // Fixed 2dp: a live timer must not make the digit count jitter
          // between 284.5 and 284.51 on a financial KPI.
          label={labelWithHint(
            'Billable hours',
            current && current.totalHours > 0 && (
              <InfoHint label="Billable share">
                <BillableShareBasis
                  billableHours={current.billableHours}
                  totalHours={current.totalHours}
                />
              </InfoHint>
            ),
          )}
          value={current ? formatHours(current.billableHours, { fixed: true }) : '—'}
          hint={current ? `of ${formatHours(current.totalHours, { fixed: true })} tracked` : undefined}
          delta={billableDelta}
          deltaSuffix={billableDelta != null ? prevLabel : undefined}
          icon={<StatIcon kind="billable" />}
          loading={utilization.isPending}
          {...utilFailed}
        >
          {current && current.totalHours > 0 && (
            <div className="mt-3">
              <div
                className="flex h-2 w-full gap-px overflow-hidden rounded-full bg-grey-100"
                role="img"
                aria-label={`${formatPercent(current.billableHours / current.totalHours, {
                  decimals: 0,
                })} of tracked time is billable`}
              >
                <span
                  className="h-full rounded-l-full bg-accent"
                  style={{ width: `${(current.billableHours / current.totalHours) * 100}%` }}
                />
                <span className="h-full flex-1 bg-grey-300" />
              </div>
              <p className="mt-1.5 flex flex-wrap items-baseline gap-x-2 text-caption text-grey-500">
                {/* ⚠ **THIS IS "BILLABLE SHARE", AND IT NAMES ITS DENOMINATOR.**
                    It is the same figure `/dashboard` used to print under the
                    words "Staff utilization" — 63% against the 49% two tiles to
                    the left, for the same 8 staff in the same month. The two
                    tiles are told apart by these three words and by
                    "billable ÷ capacity" on the other one; the paragraph that
                    used to arbitrate them is behind this tile's (i). */}
                <span className="num whitespace-nowrap">
                  <span className="font-semibold text-accent-700">
                    {formatPercent(current.billableHours / current.totalHours, { decimals: 0 })}
                  </span>{' '}
                  billable ÷ tracked
                </span>
                <span className="num whitespace-nowrap">
                  {formatHours(current.totalHours - current.billableHours, { fixed: true })} h
                  non-billable
                </span>
              </p>
            </div>
          )}
        </TeamStatCard>
        <TeamStatCard
          label="Avg h / person / wk"
          value={
            current?.avgHoursPerPersonPerWeek != null
              ? formatHours(current.avgHoursPerPersonPerWeek, { fixed: true })
              : '—'
          }
          hint={
            utilization.data
              ? typeFilter === 'ALL'
                ? `${utilization.data.activeStaff} staff · ${utilization.data.activeContractors} contract`
                : `${utilization.data.activeInScope} active`
              : undefined
          }
          delta={avgDelta}
          deltaSuffix={avgDelta != null ? prevLabel : undefined}
          icon={<StatIcon kind="hours" />}
          loading={utilization.isPending}
          {...utilFailed}
        >
          {/* Twelve-month spark of the same measure, so the number has a shape. */}
          <MonthlySpark
            className="mt-3"
            months={utilMonths}
            value={(m) => m.avgHoursPerPersonPerWeek}
            color={() => AVG_HOURS_HEX}
            window={avgWindow}
            selectedMonth={activeMonth}
            tooltip={(m, v) =>
              `${formatMonth(`${m.month}-01`)} — ${
                v != null ? `${formatHours(v, { fixed: true })} h / person / week` : 'no data'
              }`
            }
            ariaLabel={`Average tracked hours per person per week on a ${avgWindow.lo} to ${
              avgWindow.hi
            } hour scale: ${utilMonths
              .map(
                (m) =>
                  `${formatMonth(`${m.month}-01`)} ${
                    m.avgHoursPerPersonPerWeek != null
                      ? `${formatHours(m.avgHoursPerPersonPerWeek, { fixed: true })} h`
                      : 'no data'
                  }`,
              )
              .join(', ')}`}
          />
          <SparkFooter months={utilMonths} scale={`scale ${avgWindow.lo}–${avgWindow.hi} h`} />
        </TeamStatCard>
      </div>

      {/* ⚠ The arbitration between these two ratios — utilization ÷ capacity
          vs billable share ÷ tracked — used to be a four-line paragraph here.
          Each tile now names its own denominator in its hint and carries the
          explanation behind an (i) (`UtilizationBasis` / `BillableShareBasis`
          above). Do not restore the paragraph; keep the denominators. */}

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3">
        <SearchInput
          searches="people"
          value={search}
          onChange={(next) => patch({ q: next || null }, true)}
          className="w-full sm:w-60"
        />
        <SegmentedControl
          ariaLabel="Filter by type"
          size="md"
          value={typeFilter}
          onChange={setType}
          options={filterChips}
        />
        <div className="ml-auto flex items-center gap-2">
          <ArchivedToggle
            noun="people"
            active={showArchived}
            count={archivedCount}
            onChange={(next) => patch({ archived: next ? '1' : null })}
          />
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            <Download className="h-4 w-4" aria-hidden /> Export CSV
          </Button>
        </div>
      </div>

      {/* ⚠ WHO SEES A TIMER, ANSWERABLE WITHOUT OPENING TEN PEOPLE.
          A person record is not the same as tracking time: directory sync makes
          one for everyone on the agency roster, so the population here is "all
          staff" and only some of them log hours. This is the count on screen
          and the rule behind the (i) — the short-label discipline the money
          screens already follow. It is deliberately NOT a table column: the
          column caps on this table already sum close to the overflow width. */}
      {trackers.total > 0 && (
        <p className="mb-3 flex flex-wrap items-center gap-x-1.5 text-body-sm text-grey-500">
          <span className="num font-semibold text-secondary">
            {trackers.ready} of {trackers.total}
          </span>
          <span>get a timer</span>
          {trackers.waiting > 0 && (
            <span className="font-semibold text-status-partial">
              · {trackers.waiting} marked as tracking but{' '}
              {trackers.waiting === 1 ? 'has' : 'have'} no Solid account
            </span>
          )}
          {trackers.noAddress > 0 && (
            <span className="font-semibold text-status-partial">
              · {trackers.noAddress} tracking without an address on file
            </span>
          )}
          <InfoHint label="Who gets a timer" width={330}>
            <p>
              A timer appears in the Solid app bar — in every app, not just this
              one — for people tagged as tracking time. Turn it on or off on a
              person&rsquo;s page, or under Edit.
            </p>
            <p className="mt-2">
              Every tracker also has a timesheet frequency — daily, weekly,
              monthly or never — which decides the period they submit and what
              the reminder chases. Of these trackers:{' '}
              <span className="num">
                {cadenceCounts.DAILY} daily · {cadenceCounts.WEEKLY} weekly ·{' '}
                {cadenceCounts.MONTHLY} monthly · {cadenceCounts.NEVER} never
              </span>
              .
            </p>
            <p className="mt-2">
              Tracking needs an accounts.soliddigital.com account linked to the
              person — the timer signs in as them. Link one from the
              person&rsquo;s page.
            </p>
          </InfoHint>
        </p>
      )}

      {list.isPending ? (
        <Card>
          <SkeletonTable rows={8} />
        </Card>
      ) : people.length === 0 ? (
        <Card>
          <EmptyState
            icon={<UserPlus />}
            title="Build your team"
            description="Add staff and contractors with their weekly capacity, cost model and bill rates — utilization and margin reporting starts as soon as they track time."
            action={
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4" aria-hidden /> Add your first person
              </Button>
            }
          />
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Search />}
            title="No people match"
            description={
              search.trim()
                ? `Nothing matches “${search.trim()}”${
                    !showArchived && archivedCount > 0 ? ' among the active people.' : '.'
                  }`
                : 'Try a different filter.'
            }
            action={
              search.trim() ? (
                <Button variant="outline" onClick={() => patch({ q: null })}>
                  <Search className="h-4 w-4" aria-hidden /> Clear search
                </Button>
              ) : !showArchived && archivedCount > 0 ? (
                <Button variant="outline" onClick={() => patch({ archived: '1' })}>
                  Show archived ({archivedCount})
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 border-b border-grey-100 px-4 py-3">
            <div className="min-w-0">
              <p className="text-body-sm text-grey-500">
                <span className="num font-semibold text-secondary">{rows.length}</span>
                {bandFilter != null && (
                  <>
                    {' '}
                    of <span className="num">{filtered.length}</span>
                  </>
                )}{' '}
                {rows.length === 1 && bandFilter == null ? 'person' : 'people'} · {sortCaption}
                {monthInProgress && <InProgressPill className="ml-2 align-middle" />}
              </p>
              <CapacityBarKey className="mt-1" />
            </div>
            <UtilizationTriageChips
              counts={bandCounts}
              money={chipMoney}
              value={bandFilter}
              onChange={(next) => patch({ band: next })}
            />
          </div>
          {rows.length === 0 ? (
            <EmptyState
              icon={<Gauge />}
              title={bandFilter === 'negative' ? 'Everyone is above water' : 'Nobody in that band'}
              description={
                bandFilter === 'negative'
                  ? 'No one in the current view has a negative margin this month.'
                  : 'No one in the current view falls in the utilization band you picked.'
              }
              action={
                <Button variant="outline" onClick={() => patch({ band: null })}>
                  Show all {filtered.length}
                </Button>
              }
            />
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              rowKey={(p) => p.id}
              sort={sort}
              onSortChange={(next) => patch({ sort: serializeSort(next) }, true)}
              onRowClick={(p) => navigate(`/team/${p.id}`)}
            />
          )}
        </Card>
      )}

      <PersonModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        person={editing}
        onSaved={(saved) => {
          // A new (or newly linked) person: go to them — their page is where
          // the rest of the setup is read back.
          if (!editing) navigate(`/team/${saved.id}`);
        }}
      />
    </div>
  );
}
